<template>
  <div id="#add-job-form">
    <form submit.prevent id="add-job-form">
      <section class="mt-4" v-show="step == 1">
        <!-- COPY EXISTING MODAL -->
        <div class="row justify-content-center">
          <div class="col-md-11 d-flex">
            <button
              type="button"
              @click="onOpenCopyExisting"
              class="btn btn-primary btn-custom ml-auto text-nowrap"
            >
              Copy Existing
            </button>

            <CustomMainModal @close="closeModal" v-show="showModal" modalSize="modal-ml">
              <template v-slot:header>
                <h5>Copy Existing Job</h5>
              </template>

              <div class="form-group m-label-left">
                <select
                  id="empl"
                  class="form-control mr-2"
                  @change="handleCopyExistingSelectedEmployer($event)"
                  v-model="selectedEmployerModel"
                >
                  <option
                    v-for="employer in getEmployersDropdown"
                    :key="employer.id"
                    :value="{ id: employer.id, name: employer.name }"
                  >
                    {{ employer.name }}
                  </option>
                </select>
                <label for="empl">Employer</label>
              </div>
              <div class="form-group m-label-left">
                <select
                  class="form-control form-control"
                  @change="handleEmployerJobs($event)"
                  v-model="selectedJobModel"
                >
                  <option
                    v-if="getJobsForEmployer.length === 0"
                    :value="{
                      id: '',
                      title: 'No registered jobs',
                      city: { name: '' },
                      country: { name: '' },
                    }"
                  >
                    No registered jobs
                  </option>
                  <option
                    v-for="job in getJobsForEmployer"
                    :key="job.id"
                    :value="{ id: job.id, title: job.title }"
                  >
                    {{ job.title }}, {{ job.city.name }}
                  </option>
                </select>

                <label for="empl">Job</label>
              </div>

              <div class="modal-footer d-flex justify-content-center">
                <button
                  :disabled="selectedJobModel ? false : true"
                  type="button"
                  class="btn btn-primary btn-custom"
                  @click="copyExistingJob"
                >
                  Copy
                </button>
                <button type="button" class="btn btn-secondary btn-custom" @click="closeModal">
                  Cancel
                </button>
              </div>
            </CustomMainModal>
          </div>
        </div>
        <!-- END COPY EXISTING MODAL -->
        <div class="row justify-content-center">
          <div class="col-md-11">
            <!-- error msg -->
            <Message v-show="errorMessage" class="mt-2" type="error-alert">
              <p slot="alert-message" class="mb-0">{{ errorMessage }}</p>
            </Message>

            <div
              class="form-group filter-item m-label-left required-fixed-country"
              v-if="
                ['job_uuid', 'admin-jobs', 'recruiter-jobs', 'recruiter_job_uuid'].includes(
                  $route.name,
                )
              "
            >
              <v-select
                class="my-3"
                @input="handleSelectedEmployer"
                v-model="selectedEmployerModel"
                :options="getEmployersDropdownComputed"
                label="name"
                :reduce="employer => employer"
                :clearable="false"
                id="empl"
              />
              <label for="empl">Employer</label>
            </div>

            <div class="form-group m-label-left isWidth required-fixed-right">
              <input
                type="text"
                v-model.trim="$v.selectedFields.selectedJobTitle.$model"
                @change="stepValidator"
                class="form-control my-3"
                id="job-title"
                :class="{
                  'is-invalid': $v.selectedFields.selectedJobTitle.$error,
                  'is-valid': !$v.selectedFields.selectedJobTitle.$invalid,
                }"
                required
              />
              <label for="job-title">Job title</label>
            </div>

            <div class="form-group m-label-left isWidth">
              <input
                type="text"
                id="street-address"
                v-model="selectedFields.address"
                class="form-control"
                required
              />

              <label for="street-address">Street address</label>
            </div>

            <div class="row">
              <div class="form-group m-label col-md-4">
                <input
                  type="text"
                  id="postal-code"
                  v-model="selectedFields.postal"
                  class="form-control"
                  required
                  autocomplete
                />
                <label for="postal-code">Postal code</label>
              </div>
              <div class="form-group m-label filter-item col-md-8 required-fixed-city">
                <v-select
                  v-model="selectedFields.selectedCity"
                  @change="stepValidator"
                  label="name"
                  :options="getCitiesListByCountry"
                  :reduce="city => city"
                  :clearable="true"
                  id="city"
                >
                  <template v-slot:no-options>
                    <div style="opacity: 0.5">Please select a country first</div>
                  </template>
                </v-select>
                <label for="city">City</label>
              </div>
            </div>
            <!-- Countries -->
            <div class="form-group filter-item m-label-left isWidth required-fixed-country">
              <v-select
                id="country"
                v-model="$v.selectedFields.selectedCountry.$model"
                @input="
                  () => {
                    fetchCitiesByCountryId();
                    resetCity();
                  }
                "
                :options="getAllCountries"
                label="name"
                :reduce="country => country"
                :clearable="true"
                :class="{
                  'is-invalid': $v.selectedFields.selectedCountry.$error,
                  'is-valid': !$v.selectedFields.selectedCountry.$invalid,
                }"
              >
                <template v-slot:no-options>
                  <div style="opacity: 0.5">Sorry, no matching country</div>
                </template>
              </v-select>
              <label for="country">Country</label>
            </div>

            <div class="form-group m-label-left isWidth required-fixed-country">
              <select
                id="employment"
                v-model="$v.selectedFields.selectedEmploymentType.$model"
                @change="stepValidator"
                class="form-select my-3 w-100"
                :class="{
                  'is-invalid': $v.selectedFields.selectedEmploymentType.$error,
                  'is-valid': !$v.selectedFields.selectedEmploymentType.$invalid,
                }"
              >
                <option value="1">Employment</option>
                <option value="2">Self-employment</option>
                <option value="3">Employment / Self-Employment</option>
              </select>
              <label for="employment">Employment type</label>
            </div>
          </div>
        </div>
      </section>

      <section class="mt-4" v-if="step === 2">
        <div class="row justify-content-center">
          <div class="col-md-11">
            <div class="form-group m-label-left isWidth required-fixed-right">
              <input
                id="exp"
                type="text"
                v-model.trim="$v.selectedFields.selectedExperience.$model"
                @change="stepValidator"
                class="form-control"
                :class="{
                  'is-invalid': $v.selectedFields.selectedExperience.$error,
                  'is-valid': !$v.selectedFields.selectedExperience.$invalid,
                }"
                aria-describedby="MinimumYearsOfExperienceHelp"
              />
              <label for="exp">Minimum years of experience</label>
            </div>

            <div class="form-group m-label-left isWidth">
              <select
                id="degree"
                @change="checkDegree"
                v-model="selectedFields.selectedDegree"
                class="form-select my-3 w-100"
                style="box-shadow: none"
              >
                <option value="">Not required</option>
                <option :value="1">University</option>
                <option :value="2">Bachelor's degree</option>
                <option :value="3">Master's degree</option>
                <option :value="4">PhD</option>
              </select>
              <label for="degree">Degree</label>
            </div>

            <div class="form-group m-label-left isWidth">
              <input
                :disabled="selectedFields.selectedDegree === ''"
                type="text"
                class="form-control w-100"
                id="degree-field"
                v-model="selectedFields.degreeField"
              />
              <label for="degree-field">Degree field</label>
            </div>

            <div class="form-group m-label-left filter-item">
              <v-select
                v-model="selectedFields.competences"
                @search="onCompetenceSearch"
                label="name"
                :options="getCompetencesFilterList"
                :reduce="comp => comp"
                :clearable="true"
                multiple
                id="add-job-competences"
              >
                <template v-slot:no-options="{ search, searching }">
                  <template v-if="searching && search.length >= 3">
                    No results found for <em>{{ search }}</em
                    >.
                  </template>
                  <div style="opacity: 0.5" v-else>Please type at least 3 characters</div>
                </template>
              </v-select>
              <label for="competences">Required competencies</label>
            </div>

            <div class="form-group m-label-left filter-item">
              <v-select
                v-model="selectedFields.advantageCompetences"
                label="name"
                :options="[...getCompetencesFilterList]"
                :reduce="comp => comp"
                :clearable="true"
                multiple
                id="advantage"
              >
                <template v-slot:no-options="{ search, searching }">
                  <template v-if="searching && search.length >= 3">
                    No results found for <em>{{ search }}</em
                    >.
                  </template>
                  <div style="opacity: 0.5" v-else>Please type at least 3 characters</div>
                </template>
              </v-select>
              <label for="advantage">Advantage competencies</label>
            </div>
            <div class="form-group m-label-left filter-item addjob">
              <v-select
                v-model="selectedFields.languages"
                id="Languages"
                label="name"
                :options="getLanguagesList"
                :reduce="lang => lang"
                :clearable="true"
                multiple
              >
                <template v-slot:no-options="{ search, searching }">
                  <template v-if="searching && search.length >= 3">
                    No results found for <em>{{ search }}</em
                    >.
                  </template>
                  <div style="opacity: 0.5" v-else>Please type at least 3 characters</div>
                </template>
              </v-select>
              <label for="Languages">Language skills</label>
            </div>
          </div>
        </div>
      </section>

      <span class="mt-4" v-if="step === 3">
        <div class="row justify-content-center mb-4 regen">
          <div class="col-md-11 d-flex">
            <select
              style="width: 225px;"
              class="form-select"
              v-model="selectedFields.selectedLanguageForAi"
            >
              <option value="bg">Bulgarian</option>
              <option value="zh_Hans">Chinese (Simplified)</option>
              <option value="zh_Hant">Chinese (Traditional)</option>
              <option value="cs">Czech</option>
              <option value="da">Danish</option>
              <option value="nl">Dutch</option>
              <option value="en">English</option>
              <option value="fi">Finnish</option>
              <option value="fr">French</option>
              <option value="de">German</option>
              <option value="el">Greek</option>
              <option value="hu">Hungarian</option>
              <option value="it">Italian</option>
              <option value="ja">Japanese</option>
              <option value="ko">Korean</option>
              <option value="no">Norwegian</option>
              <option value="pl">Polish</option>
              <option value="pt">Portuguese</option>
              <option value="ro">Romanian</option>
              <option value="ru">Russian</option>
              <option value="es">Spanish</option>
              <option value="sv">Swedish</option>
              <option value="tr">Turkish</option>
              <option value="uk">Ukrainian</option>
            </select>

            <button
              @click="showPromp = !showPromp"
              type="button"
              class="btn btn-primary btn-custom ml-auto text-nowrap"
              style="background:transparent; border: none!important; box-shadow: none!important;"
            >
              <img style="width: 23px;" src="https://jobshark.eu/img/ai_icon.png" alt="debug" />
            </button>

            <button
              :disabled="isGeneratingAI"
              @click="generateWithAi"
              type="button"
              class="btn btn-primary btn-custom ml-2 text-nowrap"
            >
              Rewrite with AI
            </button>
            <button
              :disabled="isGeneratingAI"
              @click="translateWithAi"
              type="button"
              class="btn btn-primary btn-custom ml-2 text-nowrap"
            >
              Regenerate from Template
            </button>
          </div>
        </div>

        <div class="row justify-content-center" :key="componentKey">
          <div class="col-md-11">
            <Spinner :isLoading="isGeneratingAI" />
            <Editor v-model="jobAdText" />
            <div v-show="showPromp">
              <div v-if="selectedFields.baseChatPrompt && !isGeneratingAI" class="context mt-5">
                <strong style="display: block; margin-bottom: 12px;">Model</strong>
                <!--     <Editor v-model="selectedFields.baseChatPrompt" /> -->
                <textarea
                  style="font-size: 13px;"
                  v-model="selectedFields.baseChatPrompt"
                  class="form-control"
                  rows="10"
                  >{{ selectedFields.baseChatPrompt }}</textarea
                >
              </div>
              <div v-if="selectedFields.prompt && !isGeneratingAI" class="context mt-4">
                <strong style="display: block; margin-bottom: 12px;">Prompt</strong>
                <div
                  style="font-size: 13px; border:1px solid #DDD; padding:12px;"
                  v-html="selectedFields.prompt"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </span>

      <section class="mt-4" v-if="step === 4">
        <div class="row justify-content-center">
          <div class="col-md-11">
            <div class="form-group m-label-left isWidth required-fixed-country">
              <select
                @change="resetDuration"
                id="service-level"
                class="form-select"
                :class="{
                  'is-invalid': $v.selectedFields.selectedServiceLevel.$error,
                  'is-valid': !$v.selectedFields.selectedServiceLevel.$invalid,
                }"
                v-model.trim.lazy="$v.selectedFields.selectedServiceLevel.$model"
              >
                <option :value="0" selected>Basic</option>
                <option :value="1">Sourcing</option>
                <option :value="2">Full-service</option>
                <option :value="3">Nearshore</option>
                <option :value="4">Freelancers</option>
              </select>
              <label for="service-level">Service level</label>
            </div>

            <div class="form-group m-label-left isWidth required-fixed-right">
              <input
                v-model="selectedFields.individiuals"
                id="number"
                type="text"
                class="form-control"
              />
              <label for="number">Number of individuals needed </label>
            </div>

            <div class="form-group m-label-left isWidth required-fixed-country">
              <select
                id="duration"
                v-model="selectedFields.duration"
                class="form-select my-3 w-100"
                v-if="selectedFields.selectedServiceLevel === 4"
              >
                <option value="3">Less than 1 month</option>
                <option value="4">1-3 months</option>
                <option value="5">3-6 months</option>
                <option value="6">6-12 months</option>
                <option value="7">More than 12 months</option>
              </select>
              <select
                id="duration"
                v-model="selectedFields.duration"
                class="form-select my-3 w-100"
                v-else
              >
                <option value="1">Permanent employment</option>
                <option value="2">Time-limited employment</option>
              </select>

              <label for="duration">Duration</label>
            </div>

            <div class="form-group m-label-left isWidth required-fixed-country">
              <select
                id="full-time"
                v-model="selectedFields.fullTime"
                class="form-select my-3 w-100"
              >
                <option :value="true">Yes</option>
                <option :value="false">No</option>
              </select>
              <label for="full-time">Full-time</label>
            </div>

            <div class="form-group m-label-left isWidth required-fixed-country">
              <select
                id="workplace"
                v-model="selectedFields.workplace"
                class="form-select my-3 w-100"
              >
                <option value="1">Onsite</option>
                <option value="2">Hybrid</option>
                <option value="3">Remote</option>
              </select>
              <label for="workplace">Workplace</label>
            </div>

            <div class="form-group m-label-left isWidth required-fixed-country">
              <select
                id="relocation"
                v-model.trim.lazy="$v.selectedFields.selectedRelocation.$model"
                class="form-select my-3 w-100"
                :class="{
                  'is-invalid': $v.selectedFields.selectedRelocation.$error,
                  'is-valid': !$v.selectedFields.selectedRelocation.$invalid,
                }"
              >
                <option :value="true">Yes</option>
                <option :value="false">No</option>
              </select>
              <label for="relocation">Relocation</label>
            </div>

            <div class="form-group m-label-left isWidth required-fixed-country">
              <select
                id="promoted"
                v-model.trim.lazy="$v.selectedFields.selectedPromoted.$model"
                class="form-select"
                :class="{
                  'is-invalid': $v.selectedFields.selectedPromoted.$error,
                  'is-valid': !$v.selectedFields.selectedPromoted.$invalid,
                }"
              >
                <option :value="false">No</option>
                <option :value="true">Yes</option>
              </select>
              <label for="service-level">Promoted</label>
            </div>

            <!--           <div class="form-group textarea-hidden">
              <textarea
                v-model="selectedFields.comments"
                class="form-control add-job-comments"
                id="add-job-comments"
                rows="6"
                placeholder="Add comment for Jobshark"
                hidden
              ></textarea>
            </div> -->
          </div>
        </div>
      </section>
    </form>
    <!-- VALIDATION MESSAGES -->
    <div v-if="$v.selectedFields.selectedJobTitle.$error">
      <div
        v-if="!$v.selectedFields.selectedJobTitle.required"
        class="alert alert-danger text-center"
        role="alert"
      >
        <!-- {{ errorHandler($v.selectedFields.firstName.$error) }} -->
        Title field is required.
      </div>
    </div>

    <div v-if="$v.selectedFields.selectedCountry.$error">
      <div
        v-if="!$v.selectedFields.selectedCountry.required"
        class="alert alert-danger text-center"
        role="alert"
      >
        Country field is required.
      </div>
    </div>

    <div v-if="$v.selectedFields.selectedEmploymentType.$error">
      <div
        v-if="!$v.selectedFields.selectedEmploymentType.required"
        class="alert alert-danger text-center"
        role="alert"
      >
        Country field is required.
      </div>
    </div>

    <div v-if="$v.selectedFields.selectedExperience.$error">
      <div
        v-if="!$v.selectedFields.selectedExperience.required"
        class="alert alert-danger text-center"
        role="alert"
      >
        Minimum Years of Experience field is required.
      </div>
    </div>

    <div v-if="$v.selectedFields.selectedRelocation.$error">
      <div
        v-if="!$v.selectedFields.selectedRelocation.required"
        class="alert alert-danger text-center"
        role="alert"
      >
        Relocation field is required.
      </div>
    </div>

    <div v-if="$v.selectedFields.selectedServiceLevel.$error">
      <div
        v-if="!$v.selectedFields.selectedServiceLevel.required"
        class="alert alert-danger text-center"
        role="alert"
      >
        Service Level field is required.
      </div>
    </div>

    <!-- Nav btn's -->
    <div class="row justify-content-center mb-5 mt-4">
      <div class="col-6 text-center">
        <button
          class="btn btn-primary btn-custom w-50 mr-5 prev"
          @click="
            prev();
            stepValidator();
          "
          :disabled="step === 1 || step < 0"
        >
          <i class="fa fa-chevron-left mr-3"></i> Back
        </button>
      </div>
      <div class="col-6 text-center">
        <button
          v-if="step == 4"
          type="submit"
          class="btn btn-success btn-custom mr-2 next"
          @click="saveAd"
          :disabled="selectedFields.individiuals.length === 0"
        >
          Save
        </button>
        <button
          v-if="step == 4"
          type="submit"
          class="btn btn-secondary btn-custom next"
          @click="saveAndPublishAd"
          :disabled="selectedFields.individiuals.length === 0"
        >
          Save and Publish
        </button>

        <button
          v-else
          type="submit"
          class="btn btn-secondary btn-custom w-50 next"
          @click="
            next();
            stepValidator();
          "
          :disabled="isNextDisabled"
        >
          Next
          <i class="fa fa-chevron-right ml-3"></i>
        </button>
      </div>
    </div>
    <Spinner :isLoading="isLoading" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

import Editor from '@/components/common/TextEditor';
import Message from '@/components/AlertMessages/Message';
import CustomMainModal from '../../Modals/CustomMainModal';
import { errorService } from '../../../services/errorService';
import { required, minLength } from 'vuelidate/lib/validators';
import Spinner from '../../common/LoadingSpinner/Spinner';
import { degreeMixin } from '../../../mixins/degreeMixin';
import axios from 'axios';

// import format from 'date-fns/format';
// import moment from 'moment';

export default {
  components: {
    Editor,
    Message,
    Spinner,
    CustomMainModal,
  },
  mixins: [degreeMixin],
  data: () => ({
    selectedEmployerModel: { name: '' },
    selectedJobModel: null,
    isLoading: false,
    isGeneratingAI: false,
    isReadonly: false,
    componentKey: 0,
    showModal: false,
    hidePassword: true,
    errorMessage: '',
    signIn: false,
    step: 1,
    regenerate: false,
    isGenerating: true,
    citiesList: ['sofia'],
    /* Date data */
    dateFormat: 'D MMM YYYY',
    startDate: '',
    endDate: '',
    isActive: false,
    isLangActive: false,
    isCompetenceActive: false,
    isCompetenceAdvActive: false,
    jobAdText: '',
    employerSelectedOption: [],
    showPromp: false,
    selectedFields: {
      description: '',
      benefits: '',
      aboutUs: '',
      competences: [],
      advantageCompetences: [],
      selectedExperience: '',
      languages: [],
      selectExistingJobAd: 'Job Ad',
      website: '',

      selectedJobTitle: '',
      selectedEmploymentType: '1',
      selectedDegree: '',
      selectedRelocation: false,
      selectedServiceLevel: '1',
      comments: '',
      selectedCountry: { id: '', name: '', part_of_eu: null, phone_code: '', source: '' },
      selectedCity: { id: '', name: '', part_of_eu: null, phone_code: '', source: '' },
      countryId: '',
      degreeField: '',
      selectedPromoted: false,
      address: '',
      postal: '',
      individiuals: 1,
      fullTime: true,
      duration: 1,
      workplace: 2,
      selectedLanguageForAi: 'en',
      prompt: null,
      baseChatPrompt: '',
    },
    competencesOptions: [],
    languagesOptions: [],
    isNextDisabled: true,
    isCopied: false,
    isGeneratedOnce: false,
  }),
  validations: {
    selectedFields: {
      individiuals: {
        required,
      },
      fullTime: {
        required,
      },
      duration: {
        required,
      },
      selectedJobTitle: {
        required,
        minLength: minLength(3),
      },
      selectedCountry: {
        required,
      },
      selectedEmploymentType: {
        required,
      },
      selectedExperience: {
        required,
      },
      selectedRelocation: {
        required,
      },
      selectedServiceLevel: {
        required,
      },
      selectedPromoted: {
        required,
      },
    },
  },
  methods: {
    ...mapActions([
      'retrieveJobsForEmployerDropdown',
      'retrieveCitiesListByCountryId',
      'retrieveCompetencesList',
      'retrieveEmployerById',
      'retrieveJobAdById',
      'copyExistingJobAd',
      'AddJobAd',
      'retrieveUserProfile',
      'retrieveEmployersDropdown',
      /* 'retrieveLanguagesList', */
      /* 'retrieveAllCountries', */

      'retrieveJobAdByIdForCopyExisting',
    ]),

    getDegreeField(val) {
      if (val === 1) {
        return 'University';
      } else if (val === 2) {
        return "Bachelor's degree";
      } else if (val === 3) {
        return "Master's degree";
      } else if (val === 4) {
        return 'PhD';
      } else {
        return '';
      }
    },

    getLanguageByIso(lang) {
      const languages = {
        ar: 'Arabic',
        bg: 'Bulgarian',
        zh_Hans: 'Chinese (Simplified)',
        zh_Hant: 'Chinese (Traditional)',
        cs: 'Czech',
        da: 'Danish',
        nl: 'Dutch',
        en: 'English',
        fi: 'Finnish',
        fr: 'French',
        de: 'German',
        el: 'Greek',
        he: 'Hebrew',
        hu: 'Hungarian',
        it: 'Italian',
        ja: 'Japanese',
        ko: 'Korean',
        no: 'Norwegian',
        pl: 'Polish',
        pt: 'Portuguese',
        ro: 'Romanian',
        ru: 'Russian',
        es: 'Spanish',
        sv: 'Swedish',
        tr: 'Turkish',
        uk: 'Ukrainian',
      };

      return languages[lang] || 'Unknown Language';
    },

    resetCity() {
      this.selectedFields.selectedCity = {
        id: '',
        name: '',
        part_of_eu: null,
        phone_code: '',
        source: '',
      };
    },
    resetDuration() {
      if (this.selectedFields.selectedServiceLevel === 4) {
        this.selectedFields.duration = 3;
      } else {
        this.selectedFields.duration = 1;
      }
    },
    stepValidator() {
      // STEP 1
      if (this.step === 1) {
        if (
          this.$v.selectedFields.selectedJobTitle.$invalid === true ||
          this.$v.selectedFields.selectedEmploymentType.$invalid === true ||
          this.$v.selectedFields.cityId ||
          this.$v.selectedFields.countryId
        ) {
          this.isNextDisabled = true;
        } else {
          this.isNextDisabled = false;
        }
      }
      //STEP 2
      if (this.step === 2) {
        if (this.$v.selectedFields.selectedExperience.$invalid === true) {
          this.isNextDisabled = true;
        } else {
          this.isNextDisabled = false;
        }
      }
    },
    handleCopyExistingSelectedEmployer() {
      this.isLoading = true;

      this.retrieveJobsForEmployerDropdown({ employerId: this.selectedEmployerModel.id })
        .then(({ data }) => {
          this.isLoading = false;

          if (data.length == 0) {
            return this.$toasted.info(`There is no jobs for selected employer`, {
              theme: 'bubble',
              icon: 'fa-check-circle',
              position: 'bottom-right',
              duration: 10000,
            });
          }

          this.selectedJobModel = {
            id: this.getJobsForEmployer[0].id,
            title: this.getJobsForEmployer[0].title,
          };
        })
        .catch(() => {
          this.isLoading = false;
        });
    },
    handleEmployerJobs() {
      this.isLoading = false;

      /*  this.retrieveJobsPipeline({
        jobId: this.selectedJobModel.id,
      })
        .then(response => {
          this.isLoading = false;

          this.initDndData(response.data);

          this.isLoading = false;
        })
        .catch(err => {
          this.isLoading = false;

          throw err;
        }); */
    },
    handleSelectedEmployer(employer) {
      this.retrieveEmployerById(employer.id).then(({ data }) => {
        const { benefits, description } = data;

        this.selectedFields.selectedCountry = {
          id: data.country.id,
          name: data.country.name,
          part_of_eu: null,
          phone_code: '',
          source: '',
        };

        this.selectedFields.selectedCity = {
          id: data.city.id,
          name: data.city.name,
          part_of_eu: null,
          phone_code: '',
          source: '',
        };

        this.selectedFields.postal = data.address.postal_code;
        this.selectedFields.address = data.address.address;

        this.selectedFields.benefits = benefits;
        this.selectedFields.aboutUs = this.removeHtmlTags(description, '<div>');
        this.selectedFields.description = this.removeHtmlTags(description, '<div>');
        this.selectedFields.website = data.website;
      });
    },
    onOpenCopyExisting() {
      this.retrieveEmployersDropdown();
      this.showModal = true;
    },

    checkDegree() {
      if (this.selectedFields.selectedDegree === '') {
        this.selectedFields.degreeField = '';
      }
    },
    copyExistingJob() {
      this.retrieveJobAdByIdForCopyExisting(this.selectedJobModel.id)
        .then(({ data }) => {
          this.selectedEmployerModel = {
            city: data.city.name,
            country: data.country.name,
            id: data.js_employer_id,
            is_default: false,
            name: data.employer_name,
          };

          this.selectedFields.selectedJobTitle = data.title;
          this.selectedFields.address = data.street;
          this.selectedFields.postal = data.postal_code;
          /* New Fields */
          this.selectedFields.duration = data.duration;
          this.selectedFields.individiuals = data.individuals_number;
          this.selectedFields.fullTime = data.full_time;
          this.selectedFields.workplace = data.workplace;

          this.selectedFields.selectedCountry = {
            id: data.country.id,
            name: data.country.name,
            part_of_eu: null,
            phone_code: '',
            source: '',
          };

          this.selectedFields.selectedCity = {
            id: data.city.id,
            name: data.city.name,
            part_of_eu: null,
            phone_code: '',
            source: '',
          };

          this.selectedFields.selectedEmploymentType = data.employment_type.toString();

          this.selectedFields.selectedExperience = data.years_experience;

          this.selectedFields.selectedDegree = data.degree_type
            ? data.degree_type.toString()
            : data.degree_type;

          this.selectedFields.degreeField = data.degree_field;

          this.selectedFields.competences = [...data.required_competences];
          this.selectedFields.advantageCompetences = [...data.advantage_competences];
          this.selectedFields.languages = [...data.languages];
          this.selectedFields.selectedRelocation = data.relocation;

          this.selectedFields.selectedServiceLevel = data.service_level;
          this.selectedFields.selectedPromoted = data.promoted;

          this.selectedFields.benefits = data.benefits;
          this.selectedFields.aboutUs = data.about_us
            ? this.removeHtmlTags(data.about_us, 'b', 'u', 'p', 'br')
            : '';

          this.generateCopyExistingJobAd(data.description);
          this.fetchCitiesByCountryId();
        })
        .then(() => {
          this.closeModal();
        })
        .catch(err => {
          //   this.isCopied = false;

          this.$toasted.error(errorService(err), {
            theme: 'bubble',
            icon: 'fa-times-circle',
            position: 'bottom-right',
            duration: 8000,
          });
        });

      if (this.selectedFields.selectedEmploymentType) {
        this.isNextDisabled = false;
      }
    },
    generateCopyExistingJobAd(description) {
      this.isCopied = true;
      this.jobAdText = this.removeHtmlTags(description, 'div');
    },
    closeModal() {
      this.showModal = false;
      /*  this.selectedEmployerModel = {};*/
      this.selectedJobModel = null;
    },
    onCompetenceSearch(search, loading) {
      if (search.length >= 3) {
        loading(true);
        this.competencesOptions = this.getCompetencesFilterList;
      }

      loading(false);
    },
    /*  onLanguageSearch(search, loading) {
      if (search.length >= 3) {
        loading(true);
        this.languagesOptions = this.getLanguagesList;
      }

      loading(false);
    }, */
    fetchCitiesByCountryId() {
      this.retrieveCitiesListByCountryId({ id: this.selectedFields.selectedCountry.id });
    },
    forceRerender() {
      this.componentKey++;
      this.isReadonly = false;
    },
    saveAd() {
      this.isLoading = true;
      let anonymize = false;
      if (this.selectedFields.selectedServiceLevel === 3) {
        anonymize = true;
      }
      this.AddJobAd({
        js_employer_id: this.selectedEmployerModel.id
          ? this.selectedEmployerModel.id
          : this.$route.params.employer_id,
        title: this.selectedFields.selectedJobTitle,
        description: this.jobAdText,
        country_id: this.selectedFields.selectedCountry.id,
        city_id: this.selectedFields.selectedCity.id,
        street: this.selectedFields.address,
        postal_code: this.selectedFields.postal,
        individuals_number: this.selectedFields.individiuals,
        full_time: this.selectedFields.fullTime,
        duration: this.selectedFields.duration,
        employment_type: this.selectedFields.selectedEmploymentType,
        years_experience: this.selectedFields.selectedExperience,
        degree_type: this.selectedFields.selectedDegree,
        degree_field: this.selectedFields.degreeField,
        anonymize: anonymize,
        workplace: Number(this.selectedFields.workplace),
        competences: this.selectedFields.competences.map(c =>
          c.hasOwnProperty('competence_id') ? c.competence_id : c.id,
        ),
        advantage_competences: this.selectedFields.advantageCompetences.map(advantageCompetence =>
          advantageCompetence.hasOwnProperty('competence_id')
            ? advantageCompetence.competence_id
            : advantageCompetence.id,
        ),
        languages: this.selectedFields.languages.map(l =>
          l.hasOwnProperty('language_id') ? l.language_id : l.id,
        ),
        relocation: this.selectedFields.selectedRelocation,
        service_level: this.selectedFields.selectedServiceLevel,
        /* advertizing_start_date: this.startDate,
        advertizing_end_date: this.endDate, */
        promoted: this.selectedFields.selectedPromoted,
        comments: this.selectedFields.comments,
        saved_status: 'active',
      })
        .then(response => {
          this.isLoading = false;

          this.$toasted.success(`You successfully added a job`, {
            theme: 'bubble',
            icon: 'fa-check-circle',
            position: 'bottom-right',
            duration: 10000,
          });

          this.$router.push({ name: 'job_uuid', params: { job_uuid: response.data.id } });
        })
        .catch(err => {
          this.isLoading = false;

          this.$toasted.error(errorService(err), {
            theme: 'bubble',
            icon: 'fa-times-circle',
            position: 'bottom-right',
            duration: 8000,
          });
        });
    },
    saveAndPublishAd() {
      this.isLoading = true;
      let anonymize = false;
      if (this.selectedFields.selectedServiceLevel === 3) {
        anonymize = true;
      }
      if (this.selectedFields.individiuals === 0) {
        this.$toasted.error('Number of individuals must be at least 1', {
          theme: 'bubble',
          icon: 'fa-times-circle',
          position: 'bottom-right',
          duration: 8000,
        });
        this.isLoading = false;
        return false;
      }

      this.AddJobAd({
        js_employer_id: this.selectedEmployerModel.id
          ? this.selectedEmployerModel.id
          : this.$route.params.employer_id,
        title: this.selectedFields.selectedJobTitle,
        description: this.jobAdText,
        country_id: this.selectedFields.selectedCountry.id,
        city_id: this.selectedFields.selectedCity.id,
        street: this.selectedFields.address,
        postal_code: this.selectedFields.postal,
        employment_type: this.selectedFields.selectedEmploymentType,
        years_experience: this.selectedFields.selectedExperience,
        individuals_number: this.selectedFields.individiuals,
        full_time: this.selectedFields.fullTime,
        duration: this.selectedFields.duration,
        degree_type: this.selectedFields.selectedDegree,
        degree_field: this.selectedFields.degreeField,
        workplace: Number(this.selectedFields.workplace),
        anonymize: anonymize,
        competences: this.selectedFields.competences.map(c =>
          c.hasOwnProperty('competence_id') ? c.competence_id : c.id,
        ),
        advantage_competences: this.selectedFields.advantageCompetences.map(advantageCompetence =>
          advantageCompetence.hasOwnProperty('competence_id')
            ? advantageCompetence.competence_id
            : advantageCompetence.id,
        ),
        languages: this.selectedFields.languages.map(l =>
          l.hasOwnProperty('language_id') ? l.language_id : l.id,
        ),
        relocation: this.selectedFields.selectedRelocation,
        service_level: this.selectedFields.selectedServiceLevel,
        promoted: this.selectedFields.selectedPromoted,
        /* advertizing_start_date: this.startDate,
        advertizing_end_date: this.endDate, */
        comments: this.selectedFields.comments,
        saved_status: 'submitted',
      })
        .then(response => {
          this.isLoading = false;

          this.$toasted.success(`You successfully added a job`, {
            theme: 'bubble',
            icon: 'fa-check-circle',
            position: 'bottom-right',
            duration: 10000,
          });

          this.$router.push({ name: 'job_uuid', params: { job_uuid: response.data.id } });
          //  location.reload();
        })
        .catch(err => {
          this.isLoading = false;

          this.$toasted.error(errorService(err), {
            theme: 'bubble',
            icon: 'fa-times-circle',
            position: 'bottom-right',
            duration: 8000,
          });
        });
    },
    next() {
      this.step++;

      if (this.step === 3) {
        this.isNextDisabled = false;

        if (!this.isGeneratedOnce && !this.isCopied) {
          this.generateAd();
          this.isGeneratedOnce = true;
        }
      }

      this.forceRerender();
    },
    prev() {
      this.step--;
    },
    /*  formatDates(startDate, endDate) {
      let formattedDates = '';
      if (startDate) {
        formattedDates = format(startDate, this.dateFormat);
      }
      if (endDate) {
        formattedDates += ' - ' + format(endDate, this.dateFormat);
      }
      return formattedDates;
    }, */
    /*  monthFromNow() {
      return `${moment().format('DD MMM YYYY')} - ${moment()
        .add(1, 'months')
        .format('DD MMM YYYY')}`;
    }, */
    competencesList(competencesList) {
      const list = competencesList.map(c => c.name).join(', ');

      let final = '';

      if (list.lastIndexOf(',') !== -1) {
        final = `${list.slice(0, list.lastIndexOf(','))} and${list.slice(
          list.lastIndexOf(',') + 1,
        )}`;
      } else {
        final = list;
      }

      return final;
    },
    removeHtmlTags(adText, ...args) {
      // const regex = /(<([^>]+)>)/gi;
      const removeAllDivTags = /<[/]{0,1}(div)[^><]*>/g;

      return adText
        .replace(removeAllDivTags, '')
        .replace(/<!--.*?-->/g, '')
        .replace(/\r?\n/g, '');
    },
    addLiNodes() {
      const nodesList = [];

      const getDegreeNode = (degreeCode, field) => {
        switch (Number(degreeCode)) {
          case 1:
            return `University level studies`;
          case 2:
            return `Bachelor's degree in ${field} or similar`;
          case 3:
            return `Master's degree in ${field} or similar`;
          case 4:
            return `PhD in ${field} or similar`;
          default:
            return '';
        }
      };

      // [Years of experience]+ years of relevant working experience
      if (this.selectedFields.selectedExperience) {
        nodesList.push(
          `<li>${this.selectedFields.selectedExperience} years of relevant working experience</li>`,
        );
      }

      // Solid experience in [Skill1], [Skill2], … and [SkillN]
      if (this.selectedFields.competences.length) {
        nodesList.push(
          `<li>Solid experience in ${this.competencesList(this.selectedFields.competences)}</li>`,
        );
      }

      // If degree is selected then one of these: University level studies, Bachelor’s degree in [degree field] or similar, Master’s degree in [degree field] or similar, PhD in [degree field] or similar
      if (this.selectedFields.selectedDegree) {
        nodesList.push(
          `<li>${getDegreeNode(
            this.selectedFields.selectedDegree,
            this.selectedFields.degreeField,
          )}</li>`,
        );
      }

      // Good [Language1], [Language2], … and [LanguageN] skills verbally and in writing
      if (this.selectedFields.languages.length) {
        nodesList.push(
          `<li>Good ${this.competencesList(
            this.selectedFields.languages,
          )} skills verbally and in writing</li>`,
        );
      }

      return nodesList;
    },

    /* removeMoreHtmlTags(adText, ...args) {
      const regex = /<(\/?)(\w+)[^>]*\/?>/g;

      return adText
        .replace(regex, (_, endMark, tag) => {
          return args.includes(tag) ? '<' + endMark + tag + '>' : '';
        })
        .replace(/\r?\n/g, '')
        .replace(/<!--.*?-->/g, '');
    }, */
    removeMoreHtmlTags(adText, ...args) {
      const regex = /<(\/?)(\w+)[^>]*\/?>/g;

      return adText
        .replace(regex, (_, endMark, tag) => {
          return args.includes(tag) ? '<' + endMark + tag + '>' : '';
        })
        .replace(/\r?\n/g, '')
        .replace(/<!--.*?-->/g, '');
    },
    generateAd() {
      const nodes = this.addLiNodes();

      let employerName = this.removeMoreHtmlTags(this.selectedEmployerModel.name, '<p>');
      let employerAboutUs = this.selectedFields.aboutUs;
      /* let benefits = ''; */

      if (this.$route.name === 'employer_id') {
        employerName = this.getEmployerById.name
          ? this.removeHtmlTags(this.getEmployerById.name, '<p>')
          : '';
        employerAboutUs = this.getEmployerById.description ? this.getEmployerById.description : '';
      }

      /* const employerName = this.getEmployerById.name
        ? this.removeHtmlTags(this.getEmployerById.name, '<p>')
        : this.removeMoreHtmlTags(this.selectedEmployerModel.name, '<p>');

      const employerAboutUs = this.getEmployerById.description
        ? this.removeMoreHtmlTags(this.getEmployerById.description, '<p>', '<div>')
        : this.removeMoreHtmlTags(this.selectedFields.aboutUs, '<p>', '<div>'); */
      const previousExperience = this.selectedFields.advantageCompetences.length
        ? `<li>Have previous experience in ${this.competencesList(
            this.selectedFields.advantageCompetences,
          )}</li>`
        : '';

      const advantageSection = previousExperience
        ? `<br /><b>It's an advantage if you:</b><ul>${previousExperience}</ul>`
        : '';

      const benefitsSection =
        this.selectedFields.benefits || this.getEmployerById.benefits
          ? `<br /><b>Benefits:</b>${this.selectedFields.benefits || this.getEmployerById.benefits}`
          : '<br /><b>Benefits:</b>';

      this.jobAdText = `<p>(Introduction text)</p><br /><p><b>We believe that you have the following qualifications:</b></p><ul>${
        nodes.length ? nodes.join('') : ''
      }</ul>${advantageSection}${benefitsSection}<p></br></p><p>If you find our proposal interesting and you meet the above requirements for the position, please don't hesitate to apply.</p><br /><p>All applications will be treated strictly confidential.</p><br /><p><i>About ${employerName}: </i></p>${employerAboutUs}`;

      this.forceRerender();
    },
    reGenerateAd() {
      const nodes = this.addLiNodes();

      let employerName = this.removeMoreHtmlTags(this.selectedEmployerModel.name, '<p>');
      let employerAboutUs = this.selectedFields.aboutUs;

      if (this.$route.name === 'employer_id') {
        employerName = this.getEmployerById.name
          ? this.removeHtmlTags(this.getEmployerById.name, '<p>')
          : '';
        employerAboutUs = this.getEmployerById.description ? this.getEmployerById.description : '';
      }

      /* const employerName = this.getEmployerById.name
        ? this.removeHtmlTags(this.getEmployerById.name, '<p>')
        : this.removeMoreHtmlTags(this.selectedEmployerModel.name, '<p>'); */

      /* const employerAboutUs = this.getEmployerById.description
        ? this.removeMoreHtmlTags(this.getEmployerById.description, '<p>', '<div>')
        : this.removeMoreHtmlTags(this.selectedFields.aboutUs, '<p>', '<div>'); */

      const previousExperience = this.selectedFields.advantageCompetences.length
        ? `<li>Have previous experience in ${this.competencesList(
            this.selectedFields.advantageCompetences,
          )}</li>`
        : '';

      const advantageSection = previousExperience
        ? `<br /><b>It's an advantage if you:</b><ul>${previousExperience}</ul>`
        : '';

      const benefitsSection =
        this.selectedFields.benefits || this.getEmployerById.benefits
          ? `<br /><b>Benefits:</b>${this.selectedFields.benefits || this.getEmployerById.benefits}`
          : '<br /><b>Benefits:</b>';

      this.jobAdText = `<p>(Introduction text)</p><br /><p><b>We believe that you have the following qualifications:</b></p><ul>${
        nodes.length ? nodes.join('') : ''
      }</ul>${advantageSection}${benefitsSection}<p></br></p><p>If you find our proposal interesting and you meet the above requirements for the position, please don't hesitate to apply.</p><br /><p>All applications will be treated strictly confidential.</p><br /><p><i>About ${employerName}: </i></p>${employerAboutUs}`;
    },
    generateWithAi() {
      this.isGeneratingAI = true;
      axios
        .post(
          `${process.env.VUE_APP_API_URL}/admin/chatgpt/ask`,
          {
            js_employer_id: this.selectedEmployerModel.id,
            title: this.selectedFields.selectedJobTitle,
            company: this.selectedEmployerModel.name,
            years_experience: Number(this.selectedFields.selectedExperience),
            degree: this.getDegreeField(this.selectedFields.selectedDegree),
            degree_field: this.selectedFields.degreeField,
            competences: this.selectedFields.competences.map(c =>
              c.hasOwnProperty('competence_id') ? c.competence_id : c.id,
            ),
            competence_advantage: this.selectedFields.advantageCompetences.map(
              advantageCompetence =>
                advantageCompetence.hasOwnProperty('competence_id')
                  ? advantageCompetence.competence_id
                  : advantageCompetence.id,
            ),
            language_skills: this.selectedFields.languages.map(l =>
              l.hasOwnProperty('language_id') ? l.language_id : l.id,
            ),
            draft_description: this.jobAdText,
            language: this.getLanguageByIso(this.selectedFields.selectedLanguageForAi),
            context: this.selectedFields.baseChatPrompt,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem(
                `${process.env.VUE_APP_TOKEN_PREFIX}token`,
              )}`,
            },
          },
        )
        .then(res => {
          this.jobAdText = res.data.reply;
          /* this.selectedFields.baseChatPrompt = res.data.base_chat_prompt; */
          this.selectedFields.prompt = res.data.prompt;
          this.isGeneratingAI = false;
        })
        .catch(() => {
          this.isGeneratingAI = false;
        });
    },

    translateWithAi() {
      this.isGeneratingAI = true;
      this.reGenerateAd();
      axios
        .post(
          `${process.env.VUE_APP_API_URL}/admin/chatgpt/translate`,
          {
            text: this.jobAdText,
            language: this.getLanguageByIso(this.selectedFields.selectedLanguageForAi),
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem(
                `${process.env.VUE_APP_TOKEN_PREFIX}token`,
              )}`,
            },
          },
        )
        .then(res => {
          this.jobAdText = res.data[0];
          this.isGeneratingAI = false;
        })
        .catch(() => {
          this.isGeneratingAI = false;
        });
    },

    getContext() {
      axios
        .get(`${process.env.VUE_APP_API_URL}/admin/chatgpt/getcontext`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              `${process.env.VUE_APP_TOKEN_PREFIX}token`,
            )}`,
          },
        })
        .then(res => {
          this.selectedFields.baseChatPrompt = res.data[0];
        });
    },
  },
  computed: {
    ...mapGetters([
      'getCompetencesFilterList',
      'getCitiesListByCountry',
      'getEmployersDropdown',
      'getJobsForEmployer',
      'getAllCountries',
      'getLanguagesList',
      'getJobAdById',
      'getEmployerById',
    ]),
    getEmployersDropdownComputed() {
      return this.getEmployersDropdown;
    },
  },
  watch: {
    'selectedFields.selectedCountry': function(newSelectedCountry) {
      localStorage.addJobModalDataSelectedCountry = JSON.stringify(newSelectedCountry);
    },
    'selectedFields.selectedCity': function(newSelectedCity) {
      localStorage.addJobModalDataSelectedCity = JSON.stringify(newSelectedCity);
    },
  },
  created() {
    if (this.$route.params.employer_id) {
      this.selectedFields.selectedCountry = this.getEmployerById.city;
      this.selectedFields.selectedCity = this.getEmployerById.country;
    } /*  else {
      if (localStorage.addJobModalDataSelectedCountry) {
        const selectedCountry = JSON.parse(localStorage.addJobModalDataSelectedCountry);
        this.selectedFields.selectedCountry = selectedCountry;
      }

      if (localStorage.addJobModalDataSelectedCity) {
        const selectedCity = JSON.parse(localStorage.addJobModalDataSelectedCity);
        this.selectedFields.selectedCity = selectedCity;
      }
    } */
  },
  mounted() {
    if (this.$route.params.employer_id) {
      this.retrieveEmployerById(this.$route.params.employer_id).then(({ data }) => {
        this.selectedFields.address = data.address.address;
        this.selectedFields.postal = data.address.postal_code;
        this.selectedFields.selectedCity = data.city;
        this.selectedFields.selectedCountry = data.country;
        this.selectedEmployerModel.id = data.id;
        this.selectedEmployerModel.name = data.name;
      });
    }
    this.getContext();
    this.retrieveCompetencesList();
    this.forceRerender();
  },
};
</script>

<style lang="scss" scoped>
.textarea-hidden {
  height: 150px;
}

.form-group.required-fixed-right- {
  position: relative;
  &:after {
    content: '*';
    font-size: 22px;
    position: absolute;
    top: 0px;
    right: 10px;
    color: red;
    opacity: 0.6;
  }
}

.editor {
  box-shadow: 0px 2px 3px #0000000d;
  /* border: 1px solid #d7dae2; */
  border-radius: 4px;
  opacity: 1;
}

.social-buttons a {
  font-size: 0.875rem;
  font-weight: 400;
  box-shadow: 0px 1px 2px #00000029;
  border-radius: 4px;
}

.separator {
  position: relative;
  text-align: center;
  color: #b6b6b6;
  opacity: 0.8;

  span {
    display: inline-block;
    vertical-align: baseline;
    position: relative;
    padding: 0 20px;

    &:before,
    &:after {
      content: '';
      display: block;
      width: 5.5rem;
      position: absolute;
      top: 0.73em;
      border-top: 1px solid #b6b6b6;
    }

    &:before {
      right: 100%;
    }

    &:after {
      left: 100%;
    }
  }
}

/* MODAL */

/* Multi Step Form CSS */
.multisteps-form__progress {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
}

.multisteps-form__progress-btn {
  transition-property: all;
  transition-duration: 0.15s;
  transition-timing-function: linear;
  transition-delay: 0s;
  position: relative;
  padding-top: 20px;
  color: rgba(108, 117, 125, 0.7);
  text-indent: -9999px;
  border: none;
  background-color: transparent;
  outline: none !important;
  cursor: pointer;
}
.form-group.required-fixed-right {
  position: relative;
  &:after {
    content: '*';
    font-size: 22px;
    position: absolute;
    top: 0px;
    right: 9px;
    color: red;
    opacity: 0.6;
  }
}
.form-group.required-fixed-city {
  position: relative;
  &:after {
    content: '*';
    font-size: 22px;
    position: absolute;
    top: 0px;
    right: 25px;
    color: red;
    opacity: 0.6;
  }
}

.required-fixed-country {
  position: relative;

  &:after {
    content: '*';
    font-size: 22px;
    position: absolute;
    top: 0px;
    right: 7px;
    color: red;
    opacity: 0.6;
  }
}

.form-group {
  .form-control {
    padding-left: 8px;
  }
}

@media (min-width: 500px) {
  .multisteps-form__progress-btn {
    text-indent: 0;
  }
}

.multisteps-form__progress-btn:before {
  position: absolute;
  top: 0;
  left: 50%;
  display: block;
  width: 13px;
  height: 13px;
  content: '';
  -webkit-transform: translateX(-50%);
  transform: translateX(-50%);
  transition: all 0.15s linear 0s, -webkit-transform 0.15s cubic-bezier(0.05, 1.09, 0.16, 1.4) 0s;
  transition: all 0.15s linear 0s, transform 0.15s cubic-bezier(0.05, 1.09, 0.16, 1.4) 0s;
  transition: all 0.15s linear 0s, transform 0.15s cubic-bezier(0.05, 1.09, 0.16, 1.4) 0s,
    -webkit-transform 0.15s cubic-bezier(0.05, 1.09, 0.16, 1.4) 0s;
  border: 2px solid currentColor;
  border-radius: 50%;
  background-color: #fff;
  box-sizing: border-box;
  z-index: 3;
}

.multisteps-form__progress-btn:after {
  position: absolute;
  top: 5px;
  left: calc(-50% - 13px / 2);
  transition-property: all;
  transition-duration: 0.15s;
  transition-timing-function: linear;
  transition-delay: 0s;
  display: block;
  width: 100%;
  height: 2px;
  content: '';
  background-color: currentColor;
  z-index: 1;
}

.multisteps-form__progress-btn:first-child:after {
  display: none;
}

.multisteps-form__progress-btn.js-active {
  color: #0088cc;
}

.multisteps-form__progress-btn.js-active:before {
  -webkit-transform: translateX(-50%) scale(1.2);
  transform: translateX(-50%) scale(1.2);
  background-color: currentColor;
}

/* END MODAL */

/* Pagination */
.paginate {
  display: flex;
}

.paginate .prev {
  background: linear-gradient(90deg, #122549, #203762);
  text-align: right;
  justify-content: flex-end;
}

.paginate .next,
.paginate .prev {
  display: flex;
  align-items: center;
  width: 100%;
  height: 80px;
  width: 50%;
  padding: 0 2em;
  border: none;
  color: #fff;
  font-size: 18px;
  font-weight: 600;
  text-transform: capitalize;
  cursor: pointer;
  transition: padding 0.5s cubic-bezier(0.19, 1, 0.22, 1);
}

button {
  outline: 0;
}

/* .next i {
  margin-left: 10px !important;
  transition: ease-in-out 0.4s;
} */

/* .next:hover i {
  margin-left: 20px !important;
  transition: all 0.4s;
} */

/* .prev i {
  margin-right: 10px !important;
  transition: ease-in-out 0.4s;
} */

/* .prev:hover i {
  margin-right: 20px !important;
  transition: ease-in-out 0.4s;
} */

button:disabled {
  cursor: not-allowed;
  opacity: 0.5;

  &:hover {
    color: blueviolet;
  }
}

@media screen and (min-width: 40em) {
  .paginate .next,
  .paginate .prev {
    height: 118px;
    font-size: 32px;
  }
}

select,
input[type='number'],
input[type='text'],
input[type='search'] {
  height: 3rem;
  padding-top: 8px;
  line-height: 1.3;
}

.search-typehead {
  min-height: 3rem !important;
}

.add-job-comments {
  padding-top: 1.2rem;
}
</style>
